import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import _ from 'lodash';
import FormControl from '@material-ui/core/FormControl';
import RadioGroup from '@material-ui/core/RadioGroup';
import { FormControlLabel, Radio } from '@material-ui/core';
import {
  SettingValues,
  SENSOR_STATES,
  INCIDENTS_STRATEGIES
} from '@linearb/linjs-common';
import { myAccountSelector } from 'state/selectors/account';
import { selectedTeamSelector } from 'state/selectors/ui-state';
import MTTRConfig from 'views/root/views/home/views/settings/views/ProjectManagement/components/MTTRConfig';
import CFRConfig from 'views/root/views/home/views/settings/views/ProjectManagement/components/CFRConfig';
import {
  getAdditionalDataObject,
  validateInputs
} from 'views/root/views/home/views/settings/views/Settings/components/IncidentDetectionSetting/incidents-detection-settings.util';
import ApiTokenPmSettingIncidents from 'views/root/views/home/views/settings/views/ProjectManagement/connected-components/PMConfiguration/components/PMIncidentsSettings/components/ApiToken';
import PMIncidentsHeader from 'views/root/views/home/views/settings/views/ProjectManagement/connected-components/PMConfiguration/components/PMIncidentsSettings/components/PMIncidentsHeader';
import { getActiveTokens } from 'views/root/views/home/views/settings/views/ApiTokens/api-tokens.util';
import Text from 'components/base/Text';
import useSettingsService from 'components/hooks/useSettingsService';
import { StyledRadio } from './pmIncidentsSettings.style';
import { createRestoreDefaultModalOptions } from './modals.utils';
import { pmIntegrationSelector } from 'state/selectors/sensors-authorizations';
import { validateConfigurations } from './PMIncidentSettings.service';
import { useParentConfiguration } from './hooks/useParentConfiguration';
import { takeParentIncidentsSettingsModalOptions } from 'modals/modal-options-factory';
import { useSaveIncidentsSettings } from './hooks/useSaveIncidentsSettings';

const DEFAULT_VALUE = { key: INCIDENTS_STRATEGIES.FILTERED_ISSUES };

export const PmIncidentsSubTitleRadio = ({ text = '' }) => (
  <Text
    lineHeight={1.7}
    size='medium'
    color='shuttleGray'
    letterSpacing='0.2px'
    weight='400'
    mb='1rem'
  >
    {text}
  </Text>
);

const PMIncidentsSettings = ({
  apiTokens,
  setApiTokens,
  toggleModal,
  toggleSnackBar,
  isOrgSettings
}) => {
  const pmSensor = useSelector(pmIntegrationSelector) || {};
  const isAuthorized = pmSensor?.state === SENSOR_STATES.ok;
  const selectedTeam = useSelector(selectedTeamSelector);
  const account = useSelector(myAccountSelector);
  const history = useHistory();
  const isTeamSetting = !isOrgSettings;

  const settingName = SettingValues.INCIDENTS_STRATEGY;
  const settingLevel = isTeamSetting ? 'team' : 'organization';
  const entityId = isTeamSetting ? selectedTeam?.id : undefined;

  const { updateSetting, getSettingOrDefinitionValue } = useSettingsService();

  const incidentsStrategy = getSettingOrDefinitionValue({
    name: SettingValues.INCIDENTS_STRATEGY,
    definitionLevel: settingLevel,
    ...(!isOrgSettings && { teamId: selectedTeam?.id })
  });

  const [value, setValue] = useState(incidentsStrategy);
  const [selectedRadio, setSelectedRaio] = useState(incidentsStrategy?.key);
  const [cfrConfiguration, setCfrConfiguration] = useState(null);
  const [mttrConfiguration, setMttrConfiguration] = useState(null);

  useEffect(() => {
    setIsSaveEnabled(
      validateConfigurations(cfrConfiguration, mttrConfiguration)
    );
  }, [mttrConfiguration, cfrConfiguration]);

  const onTokenGenerated = generateToken => {
    setValue({
      ...value,
      api_token_id: generateToken.id
    });
  };

  const activeTokens = React.useMemo(
    () => getActiveTokens(apiTokens),
    [apiTokens]
  );
  const tokenExists = activeTokens && activeTokens.length;

  const handleRadioChange = radioValue => {
    setSelectedRaio(radioValue);
    const additionalDataObject = getAdditionalDataObject({
      radioValue,
      tokenExists,
      activeTokens
    });
    setValue(oldValue => ({
      ...oldValue,
      key: radioValue,
      ...additionalDataObject
    }));
  };

  const onChange = e => {
    const radioValue = e.target.value;
    radioValue === selectedRadio ? _.noop() : handleRadioChange(radioValue);
  };

  const {
    getParentName,
    parentIncidentsStrategy,
    parentCFRConfig,
    parentMTTRConfig
  } = useParentConfiguration();

  const parentName = getParentName();

  const {
    saveSettings,
    isSaveEnabled,
    setIsSaveEnabled,
    isUsingParentConfig,
    onSaveParentConfig
  } = useSaveIncidentsSettings({
    cfrConfiguration,
    mttrConfiguration,
    isOrgSettings,
    value
  });

  const handleUseParentConfig = async () => {
    toggleModal(
      takeParentIncidentsSettingsModalOptions({
        onSaveParentConfig,
        toggleModal,
        parentName
      })
    );
  };

  const restoreToDefault = async () => {
    try {
      await updateSetting(settingName, null, settingLevel, entityId, true);
      setValue(DEFAULT_VALUE);
      setSelectedRaio(DEFAULT_VALUE.key);
    } catch (e) {}
  };

  const handleRestoreToDefault = () => {
    toggleModal(
      createRestoreDefaultModalOptions({
        restoreToDefault,
        toggleModal
      })
    );
  };

  const showCfrMttr =
    isAuthorized && selectedRadio === INCIDENTS_STRATEGIES.FILTERED_ISSUES;

  useEffect(() => {
    setIsSaveEnabled(validateInputs(incidentsStrategy, value));
  }, [value]);

  useEffect(() => {
    if (isUsingParentConfig) {
      setValue(parentIncidentsStrategy);
      setSelectedRaio(parentIncidentsStrategy.key);
    } else if (incidentsStrategy?.key !== selectedRadio) {
      setValue(incidentsStrategy);
      setSelectedRaio(incidentsStrategy?.key);
    }
  }, [
    incidentsStrategy,
    selectedTeam,
    parentIncidentsStrategy,
    isUsingParentConfig
  ]);

  useEffect(() => {
    if (isUsingParentConfig) {
      setIsSaveEnabled(false);
    }
  }, [isUsingParentConfig]);

  return (
    <>
      <PMIncidentsHeader
        disableSave={!isSaveEnabled}
        onSave={saveSettings}
        isTeamSetting={isTeamSetting}
        parentName={parentName}
        onUseParentConfig={handleUseParentConfig}
        isUsingParentConfig={isUsingParentConfig}
        onRestore={handleRestoreToDefault}
      />
      <FormControl>
        <RadioGroup
          aria-labelledby='demo-radio-buttons-group-label'
          name='radio-buttons-group'
        >
          <FormControlLabel
            value={INCIDENTS_STRATEGIES.REPORTED}
            onChange={onChange}
            className='topAligned'
            label={
              <StyledRadio>
                <ApiTokenPmSettingIncidents
                  account={account}
                  value={value}
                  isShowTokenField={
                    selectedRadio === INCIDENTS_STRATEGIES.REPORTED
                  }
                  apiTokens={apiTokens}
                  setApiTokens={setApiTokens}
                  onTokenGenerated={onTokenGenerated}
                  toggleModal={toggleModal}
                  toggleSnackBar={toggleSnackBar}
                  tokenExists={tokenExists}
                />
              </StyledRadio>
            }
            control={
              <Radio
                checked={selectedRadio === INCIDENTS_STRATEGIES.REPORTED}
                color='primary'
              />
            }
          />
          <FormControlLabel
            value={INCIDENTS_STRATEGIES.FILTERED_ISSUES}
            onChange={onChange}
            className='topAligned'
            label={
              <StyledRadio>
                <Text lineHeight={1.7} size='medium' weight='bold'>
                  {!isAuthorized
                    ? 'None'
                    : 'Detect my incidents based on critical bugs'}
                </Text>
                <PmIncidentsSubTitleRadio
                  text={
                    !isAuthorized
                      ? ''
                      : 'From first commit made to a branch to when a pull request is opened.'
                  }
                />
              </StyledRadio>
            }
            control={
              <Radio
                checked={selectedRadio === INCIDENTS_STRATEGIES.FILTERED_ISSUES}
                color='primary'
              />
            }
          />
        </RadioGroup>
      </FormControl>
      {showCfrMttr && (
        <>
          <CFRConfig
            teamId={isTeamSetting ? selectedTeam?.id : null}
            history={history}
            toggleSnackBar={toggleSnackBar}
            setConfiguration={setCfrConfiguration}
            parentConfiguration={parentCFRConfig}
            isUsingParentConfig={isUsingParentConfig}
            isOrgSettings={isOrgSettings}
          />
          <MTTRConfig
            teamId={isTeamSetting ? selectedTeam?.id : null}
            history={history}
            toggleSnackBar={toggleSnackBar}
            setConfiguration={setMttrConfiguration}
            parentConfiguration={parentMTTRConfig}
            isUsingParentConfig={isUsingParentConfig}
            isOrgSettings={isOrgSettings}
          />
        </>
      )}
    </>
  );
};

export default PMIncidentsSettings;
